/** Ordered multi-sort model helpers (Item A — multi-sort).
 *
 * Pure + unit-tested: they translate between AG-Grid's columnState (a `sort` + `sortIndex` per
 * column) and a priority-ordered `SortLevel[]` (index 0 = primary sort key), and provide the
 * add / remove / reorder / set-direction operations the Sort panel drives. The grid's columnState
 * stays the source of truth (persisted in the saved view for free — crmViewConfig already normalizes
 * `sort` + `sortIndex`); these helpers never touch the grid — the caller applies the result via
 * `applyColumnState`. Mirrors the backend `_apply_sort` (lists.py) contract: the array order IS the
 * ORDER BY precedence, so a stable multi-key sort falls out for free on the SSRM cutover.
 */

export type SortDir = 'asc' | 'desc';
export interface SortLevel {
  colId: string;
  sort: SortDir;
}

interface ColStateLike {
  colId?: string;
  sort?: string | null;
  sortIndex?: number | null;
}

/** The priority-ordered active sort model derived from AG column state: keep only the sorted
 * columns, order by `sortIndex` (AG can leave gaps after a mid-list clear), and tie-break by the
 * state's own array order so a single sort with a null `sortIndex` still resolves deterministically. */
export function readSortModel(state: ColStateLike[] | null | undefined): SortLevel[] {
  if (!Array.isArray(state)) return [];
  return state
    .map((s, i) => ({ s, i }))
    .filter(({ s }) => !!s.colId && (s.sort === 'asc' || s.sort === 'desc'))
    .sort((a, b) => {
      const ai = a.s.sortIndex ?? a.i;
      const bi = b.s.sortIndex ?? b.i;
      return ai - bi || a.i - b.i;
    })
    .map(({ s }) => ({ colId: s.colId as string, sort: s.sort as SortDir }));
}

/** The columnState patch that makes `model` the grid's full ordered sort. Pair it with
 * `defaultState: { sort: null, sortIndex: null }` on `applyColumnState` so EVERY other column's
 * sort clears — this is the single "set the whole sort model" operation (also clears all when
 * `model` is empty). */
export function sortModelColumnState(
  model: SortLevel[],
): Array<{ colId: string; sort: SortDir; sortIndex: number }> {
  return model.map((s, i) => ({ colId: s.colId, sort: s.sort, sortIndex: i }));
}

/** Append a column as the lowest-priority sort level, or update its direction if already present. */
export function addSort(model: SortLevel[], colId: string, sort: SortDir = 'asc'): SortLevel[] {
  if (model.some((s) => s.colId === colId)) {
    return model.map((s) => (s.colId === colId ? { ...s, sort } : s));
  }
  return [...model, { colId, sort }];
}

/** Drop a column from the sort model (no-op returns a fresh array; harmless). */
export function removeSort(model: SortLevel[], colId: string): SortLevel[] {
  return model.filter((s) => s.colId !== colId);
}

/** Set a level's direction explicitly (asc / desc). */
export function setDir(model: SortLevel[], colId: string, sort: SortDir): SortLevel[] {
  return model.map((s) => (s.colId === colId ? { ...s, sort } : s));
}

/** Flip a level's direction. */
export function toggleDir(model: SortLevel[], colId: string): SortLevel[] {
  return model.map((s) =>
    s.colId === colId ? { ...s, sort: s.sort === 'asc' ? 'desc' : 'asc' } : s,
  );
}

/** Move a level up (toward primary) or down (toward least-significant) one slot. Returns the SAME
 * reference (a no-op) at either edge or when `colId` isn't in the model. */
export function moveSort(model: SortLevel[], colId: string, dir: 'up' | 'down'): SortLevel[] {
  const idx = model.findIndex((s) => s.colId === colId);
  if (idx < 0) return model;
  const to = dir === 'up' ? idx - 1 : idx + 1;
  if (to < 0 || to >= model.length) return model;
  const next = [...model];
  [next[idx], next[to]] = [next[to], next[idx]];
  return next;
}
